import React, { useState } from 'react';
import { Button, Card, Form, InputNumber, message, Space, Typography } from 'antd';
import { RiseOutlined } from '@ant-design/icons';
import { DataService } from '../../config/data/dataService';

const { Text } = Typography;

const UpdateBidCard = ({ app, onUpdateBid, onBidUpdated, now = Date.now() }) => {
  const [form] = Form.useForm();
  const [updatingBid, setUpdatingBid] = useState(false);
  const [currentBid, setCurrentBid] = useState(Number(app?.quotedAmount || app?.bidAmount) || 0);
  const highestBid = Number(app?.highestBid) || 0;
  const minimumBid = highestBid + 100;
  const canUpdateBid = ['pending', 'received'].some((status) => (
    String(app?.status || '').toLowerCase().includes(status)
  ));

  const expiresAt = new Date(app?.expiresAt).getTime();
  const hasExpiry = Number.isFinite(expiresAt);
  const bidEnded = hasExpiry && expiresAt <= now;
  if (bidEnded || (!hasExpiry && !canUpdateBid)) return null;

  const handleBidUpdate = async (values) => {
    if (hasExpiry && expiresAt <= Date.now()) {
      message.warning('This bid has ended');
      return;
    }
    setUpdatingBid(true);

    try {
      const payload = {
        reference_no: app.referenceNo || app.id,
        status: 'accepted',
        bid_amount: values.bidAmount,
        reason: '',
      };
      const response = onUpdateBid
        ? await onUpdateBid(payload)
        : await DataService.post('/updateExchangeStatus', payload);

      if (response?.data?.status === true) {
        setCurrentBid(Number(values.bidAmount));
        onBidUpdated?.(Number(values.bidAmount));
        message.success('Bid updated successfully');
        return;
      }

      message.error(response?.data?.message || 'Unable to update bid');
    } catch (error) {
      console.error('History bid update error:', error);
      message.error('Unable to update bid');
    } finally {
      setUpdatingBid(false);
    }
  };

  return (
    <Card
      title={(
        <Space>
          <RiseOutlined style={{ color: '#fa8c16' }} />
          <Text strong style={{ fontSize: '18px' }}>Update Bid</Text>
        </Space>
      )}
      variant="borderless"
      style={{
        boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
        borderRadius: '16px',
        border: '1px solid #f0f0f0',
      }}
      styles={{ body: { padding: '24px' } }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
          <Text type="secondary">Current Bid</Text>
          <Text strong style={{ color: '#52c41a', textAlign: 'right' }}>
            {currentBid ? `₹${currentBid.toLocaleString('en-IN')}` : 'Unavailable'}
          </Text>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #fff7e6 0%, #fffbe6 100%)',
          border: '1px solid #ffd591',
          borderRadius: '10px',
          padding: '14px 16px',
          textAlign: 'center',
        }}>
          <Text type="secondary" style={{ display: 'block', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
            Highest Bid
          </Text>
          <Text strong style={{ color: '#d46b08', display: 'block', fontSize: '28px', lineHeight: 1.15 }}>
            ₹{highestBid.toLocaleString('en-IN')}
          </Text>
        </div>
        <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>
          Enter at least ₹{minimumBid.toLocaleString('en-IN')}.
        </Text>
        <Form form={form} layout="vertical" onFinish={handleBidUpdate}>
          <Form.Item
            name="bidAmount"
            label="New Bid Amount"
            extra="This amount includes 5% commission."
            rules={[
              { required: true, message: 'Please enter bid amount' },
              {
                validator: (_, value) => {
                  if (!value || Number(value) >= minimumBid) return Promise.resolve();
                  return Promise.reject(new Error(`Bid amount should be at least ₹${minimumBid.toLocaleString('en-IN')}`));
                },
              },
            ]}
          >
            <InputNumber
              prefix={<Text strong style={{ color: '#8c8c8c', marginRight: '4px' }}>₹</Text>}
              placeholder="Enter new bid amount"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={updatingBid}>
            Update Bid
          </Button>
        </Form>
      </Space>
    </Card>
  );
};

export default UpdateBidCard;
